import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { useContext, useMemo } from 'react';
import * as styles from './MentionBase.module.scss';
import type { PillSize, PillType } from '../../components/Pill';
import Pill, { PILL_TYPES } from '../../components/Pill';
import addTestId from '../../utils/addTestId';
import truncate from '../../utils/text/truncate';
import safeParseUrl from '../../utils/url/safeParseUrl';
import truncateUrl from '../../utils/url/truncateUrl';
import type { IntegrationMentionType } from '../types';
import { isOrganizationOrigin } from '../utils/matchCurrentUrl';

export const TRUNCATION_LENGTHS = {
  comment: 20,
  activity: 15,
  editor: 35,
} as const;

const MENTION_SIZES: Record<string, PillSize> = {
  comment: 'small',
  activity: 'small',
  editor: 'normal',
};

export type ContainerType = keyof typeof TRUNCATION_LENGTHS;

const DEFAULT_TRUNCATE_LENGTH = 35;
const URL_TRUNCATION_ENDING_LENGTH = 5;

export const shouldTruncate = (type: string) => {
  return type !== 'user';
};

const truncateMentionContent = (content: string, maxLength: number) => {
  const isUrl = content.startsWith('http');

  if (isUrl) {
    return truncateUrl(content, maxLength, URL_TRUNCATION_ENDING_LENGTH);
  }

  return truncate(content, maxLength);
};

const MentionBaseContext = React.createContext<{
  truncateLength: number;
  pillSize?: PillSize;
}>({ truncateLength: DEFAULT_TRUNCATE_LENGTH, pillSize: undefined });

interface MentionBaseProviderProps {
  children: ReactNode;
  containerType: ContainerType;
}

export const MentionBaseProvider = ({
  children,
  containerType,
}: MentionBaseProviderProps) => {
  const value = useMemo(
    () => ({
      truncateLength: TRUNCATION_LENGTHS[containerType],
      pillSize: MENTION_SIZES[containerType],
    }),
    [containerType],
  );

  return (
    <MentionBaseContext.Provider value={value}>
      {children}
    </MentionBaseContext.Provider>
  );
};

export const MENTION_CONTAINER_CLASS = styles.container;

const getPillType = (type: string): PillType => {
  if (PILL_TYPES.includes(type as PillType)) {
    return type as PillType;
  }
  return 'external';
};

type MentionBaseProps = {
  type: PillType | IntegrationMentionType;
  to?: string;
  content?: string;
  icon?: ReactNode | string;
  iconAfter?: ReactNode;
  closed?: boolean;
  newTab?: boolean;
  innerHeadRef?: React.Ref<HTMLSpanElement>;
  size?: PillSize;
  selected?: boolean;
  muted?: boolean;
};

const MentionBase = ({
  type,
  to,
  newTab = false,
  content = '',
  icon,
  iconAfter,
  closed = false,
  innerHeadRef,
  selected,
  muted,
  ...props
}: MentionBaseProps) => {
  const { truncateLength, pillSize } = useContext(MentionBaseContext);
  const truncatedContent = shouldTruncate(type)
    ? truncateMentionContent(content, truncateLength)
    : content;
  const size = props.size ?? pillSize;

  const finalContentClassName =
    classNames({ [styles.closed]: closed }) || undefined;

  const toUrl = to ? safeParseUrl(to, window.location.href) : undefined;
  const shouldOpenInNewTab =
    !!to && (newTab || !toUrl || !isOrganizationOrigin(toUrl));

  return (
    <Pill
      type={getPillType(type)}
      size={size}
      to={to}
      newTab={shouldOpenInNewTab ? 'ugc' : undefined}
      aria-label={content}
      className={classNames(
        MENTION_CONTAINER_CLASS,
        finalContentClassName,
        styles[type],
      )}
      icon={icon}
      iconAfter={iconAfter}
      innerHeadRef={innerHeadRef}
      selected={selected}
      muted={muted}
      {...addTestId('mentionContainer')}
    >
      {truncatedContent}
    </Pill>
  );
};

export default MentionBase;
